import { onMounted, onUnmounted, type Ref } from 'vue';

interface PanelRevealOptions {
    initialDelay?: number;
    stagger?: number;
}

// Finds all [data-panel-reveal] elements within a container and sequentially
// adds .is-revealed with a staggered delay. Handles async-loaded children via
// MutationObserver. Honors prefers-reduced-motion. Ported from the
// certifid-vision prototype.
export function usePanelReveal(
    containerRef: Ref<HTMLElement | null>,
    options: PanelRevealOptions = {},
) {
    const { initialDelay = 220, stagger = 120 } = options;
    let revealedCount = 0;
    let observer: MutationObserver | null = null;

    const revealElements = (elements: Element[], baseDelay: number) => {
        elements.forEach((el, index) => {
            setTimeout(() => {
                el.classList.add('is-revealed');
            }, baseDelay + index * stagger);
        });
    };

    onMounted(() => {
        if (!containerRef.value) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const revealNew = () => {
            if (!containerRef.value) return;
            const all = containerRef.value.querySelectorAll('[data-panel-reveal]');
            const unrevealed = Array.from(all).filter((el) => !el.classList.contains('is-revealed'));

            if (unrevealed.length === 0) return;

            if (reducedMotion) {
                unrevealed.forEach((el) => el.classList.add('is-revealed'));
                revealedCount = all.length;
                return;
            }

            const baseDelay = revealedCount === 0 ? initialDelay : stagger;
            revealElements(unrevealed, baseDelay);
            revealedCount = all.length;
        };

        requestAnimationFrame(() => {
            revealNew();
        });

        observer = new MutationObserver(() => {
            revealNew();
        });

        observer.observe(containerRef.value, {
            childList: true,
            subtree: true,
        });
    });

    onUnmounted(() => {
        observer?.disconnect();
        observer = null;
    });
}
